// FILE: src/components/app/layout/AppSidebar.jsx
import React, { useEffect, useRef, useMemo, useState, useCallback } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGauge,            // Dashboard
  faMagnifyingGlass,  // Search
  faArrowsLeftRight,  // Transactions
  faChartLine,        // Insights
  faPiggyBank,        // Budgets
  faChartArea,        // Forecast
  faListCheck,        // Rules
  faBuildingColumns,  // Accounts
  faTags,             // Tags
  faFileArrowUp,      // Upload Data
  faGear,             // Settings
  faDatabase,         // Data Management (admin only)
  faAngleLeft,        // Collapse toggle
  faAngleRight,       // Expand toggle
  faFileInvoiceDollar,       // Income Tax
} from "@fortawesome/free-solid-svg-icons";
import styles from "./AppSidebar.module.css";
import { useAuth } from "../../../context/AuthContext";

const PRIMARY_ITEMS = [
  { to: "/app/dashboard",    label: "Dashboard",    icon: faGauge },
  // { to: "/app/search",       label: "Search",       icon: faMagnifyingGlass },
  { to: "/app/transactions", label: "Transactions", icon: faArrowsLeftRight },
  { to: "/app/insights",     label: "Insights",     icon: faChartLine },
  { to: "/app/budgets",      label: "Budgets",      icon: faPiggyBank },
  { to: "/app/forecast",     label: "Forecast",     icon: faChartArea },,
  { to: "/app/in-tax",     label: "Income Tax Manager",     icon: faFileInvoiceDollar },
];

const SYSTEM_ITEMS = [
  { to: "/app/rules",    label: "Rules",        icon: faListCheck },
  { to: "/app/accounts", label: "Accounts",     icon: faBuildingColumns },
  { to: "/app/tags",     label: "Tags",         icon: faTags },
  { to: "/app/upload",   label: "Upload Data",  icon: faFileArrowUp },
  { to: "/app/settings", label: "Settings",     icon: faGear },
];

const ADMIN_ITEMS = [
  { to: "/app/datamgt", label: "Data Management", icon: faDatabase, adminOnly: true },
];

const isDashboardPath = (p) => p === "/app" || p === "/app/" || p === "/app/dashboard";
// Bootstrap lg breakpoint (992px)
const isDesktop = () => typeof window !== "undefined" && window.matchMedia("(min-width: 992px)").matches;
const LS_KEY = "pp.sidebar.collapsed";
const DEBUG = String(process.env.REACT_APP_DEBUG_SIDEBAR || "") === "1";
const dbg = (...args) => DEBUG && console.debug("[Sidebar]", ...args);

export default function AppSidebar({ insideOffcanvas = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pendingToRef = useRef(null);

  // Read everything from your context — no direct Firebase work here.
  const { isAdmin, appUser, user, initializing, betaStatus } = useAuth();

  // Admin is strictly provided by context; allow appUser.admin as a passive mirror
  const resolvedIsAdmin = isAdmin === true || appUser?.admin === true;

  // Desktop-only collapse state (web only). Offcanvas/mobile stays expanded.
  const [collapsed, setCollapsed] = useState(false);

  // Initialize collapse from localStorage (desktop only)
  useEffect(() => {
    if (insideOffcanvas) return;
    if (!isDesktop()) { setCollapsed(false); return; }
    try {
      const saved = localStorage.getItem(LS_KEY);
      if (saved === "1") setCollapsed(true);
    } catch {}
  }, [insideOffcanvas]);

  // Keep collapse disabled when viewport < lg
  useEffect(() => {
    if (insideOffcanvas) return;
    const mql = window.matchMedia("(min-width: 992px)");
    const onChange = () => { if (!mql.matches) setCollapsed(false); };
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, [insideOffcanvas]);

  const toggleCollapsed = useCallback(() => {
    if (insideOffcanvas || !isDesktop()) return;
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(LS_KEY, next ? "1" : "0"); } catch {}
      return next;
    });
  }, [insideOffcanvas]);

  // Offcanvas deferred navigation (unchanged)
  useEffect(() => {
    if (!insideOffcanvas) return;
    const el = document.getElementById("appSidebar");
    if (!el) return;
    const onHidden = () => {
      const to = pendingToRef.current;
      pendingToRef.current = null;
      if (to) navigate(to);
    };
    el.addEventListener("hidden.bs.offcanvas", onHidden);
    return () => el.removeEventListener("hidden.bs.offcanvas", onHidden);
  }, [insideOffcanvas, navigate]);

  useEffect(() => {
    dbg("ctx", { initializing, betaStatus, hasUser: !!user, isAdmin, appUserAdmin: appUser?.admin });
  }, [initializing, betaStatus, user, isAdmin, appUser?.admin]);

  const Section = ({ title, items, showSeparator }) => {
    if (!items || items.length === 0) return null;
    return (
      <div className={styles.section}>
        {collapsed ? (
          <>
            {showSeparator && <hr className={styles.groupRule} />}
          </>
        ) : (
          <div className={`${styles.sectionTitle} text-uppercase text-secondary small fw-bold`}>
            {title}
          </div>
        )}

        <ul className="nav nav-pills flex-column gap-1">
          {items.map((it) => {
            const isActive =
              it.to === "/app/dashboard"
                ? isDashboardPath(location.pathname)
                : location.pathname === it.to;

            return (
              <li className="nav-item" key={it.to}>
                <NavLink
                  to={it.to}
                  className={`nav-link ${styles.link} ${isActive ? "active disabled" : ""} ${collapsed ? styles.linkCollapsed : ""}`}
                  title={collapsed ? it.label : undefined}
                  {...(insideOffcanvas ? { "data-bs-dismiss": "offcanvas" } : {})}
                  onClick={(e) => {
                    if (isActive) { e.preventDefault(); e.stopPropagation(); return; }
                    if (insideOffcanvas) { e.preventDefault(); pendingToRef.current = it.to; }
                  }}
                >
                  <FontAwesomeIcon icon={it.icon} className={styles.icon} fixedWidth />
                  <span className={styles.label}>{it.label}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <aside
      className={[
        styles.wrap,
        "bg-body",
        insideOffcanvas ? styles.offcanvasWrap : "",
        collapsed ? styles.collapsed : "",
      ].join(" ")}
    >
      {/* Header + collapse toggle (desktop only) */}
      <div className={styles.header}>
        {/* Brand is hidden on mobile via CSS; short text when collapsed */}
        <span className={styles.brand} aria-hidden={collapsed}>
          {/* {!collapsed ? "Penny Pilot" : "PP"} */}
        </span>

        {!insideOffcanvas && (
          <button
            type="button"
            className={styles.collapseBtn}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand" : "Collapse"}
            onClick={toggleCollapsed}
          >
            <FontAwesomeIcon icon={collapsed ? faAngleRight : faAngleLeft} />
          </button>
        )}
      </div>

      <div className={styles.body}>
        <Section title="Main" items={PRIMARY_ITEMS} showSeparator={false} />
        <Section title="System" items={SYSTEM_ITEMS} showSeparator />

        {/* Admin section strictly from context */}
        {resolvedIsAdmin && <Section title="Admin" items={ADMIN_ITEMS} showSeparator />}

        {DEBUG && (
          <div style={{ fontSize: 11, opacity: 0.6, padding: "8px" }}>
            <div><strong>Admin (context):</strong> {String(isAdmin)}</div>
            <div><strong>Admin (appUser.admin):</strong> {String(appUser?.admin)}</div>
            <div><strong>initializing:</strong> {String(initializing)}</div>
            <div><strong>betaStatus:</strong> {String(betaStatus)}</div>
          </div>
        )}
      </div>
    </aside>
  );
}
